import { createStore, createEvent, createEffect, sample } from "effector";
import { ChatState, ChatMessage, ChatConversation, ChatUser, BlockStatus, BlockUserPayload } from "../types";
import { chatService } from "../service/chatService";

// ============ Events ============

export const fetchConversationHistoryRequested = createEvent<string>(); // conversationId
export const loadMoreMessagesRequested = createEvent<string>(); // conversationId
export const fetchConversationsRequested = createEvent();
export const loadMoreConversationsRequested = createEvent();
export const sendMessageRequested = createEvent<{
  conversationId: string;
  message: string;
  fid?: number;
}>();
export const markConversationAsReadRequested = createEvent<string>(); // conversationId
export const deleteConversationRequested = createEvent<string>(); // conversationId
export const blockUserRequested = createEvent<BlockUserPayload>();
export const setCurrentConversation = createEvent<string | null>();
export const clearConversationMessages = createEvent<string>(); // conversationId
export const setConversationSearch = createEvent<string>(); // search query

// ============ Effects ============

export const fetchConversationHistoryFx = createEffect(
  async ({ conversationId, page }: { conversationId: string; page: number }): Promise<{
    conversationId: string;
    messages: ChatMessage[];
    otherUser?: ChatUser;
    blockStatus: BlockStatus | undefined;
    hasMore: boolean;
    page: number;
  }> => {
    const result = await chatService.getConversationHistory(conversationId, page);
    return { conversationId, ...result };
  }
);

export const blockUserFx = createEffect(
  async (payload: BlockUserPayload & { conversationId: string }): Promise<{
    conversationId: string;
    blockStatus: BlockStatus;
  }> => {
    const { conversationId, ...apiPayload } = payload;
    await chatService.blockUser(apiPayload);
    return {
      conversationId,
      blockStatus: payload.mode === "block" ? "has_blocked" : "not_blocked",
    };
  }
);

export const fetchConversationsFx = createEffect(
  async ({ page, search }: { page: number; search: string }): Promise<{ conversations: ChatConversation[]; hasMore: boolean; unreadCount: number; page: number }> => {
    const result = await chatService.getAllConversations(page, search);
    return { ...result, page };
  }
);

export const sendMessageFx = createEffect(
  async ({
    conversationId,
    message,
    fid,
  }: {
    conversationId: string;
    message: string;
    fid?: number;
  }): Promise<{ conversationId: string; message: ChatMessage | null }> => {
    const sent = await chatService.sendMessage(parseInt(conversationId, 10), message, fid);
    return { conversationId, message: sent };
  }
);

export const markConversationAsReadFx = createEffect(
  async (conversationId: string): Promise<string> => {
    await chatService.markAsRead(conversationId);
    return conversationId;
  }
);

export const deleteConversationFx = createEffect(
  async (conversationId: string): Promise<string> => {
    await chatService.deleteConversation(conversationId);
    return conversationId;
  }
);

// ============ Store ============

const initialState: ChatState = {
  messagesByConversation: {},
  messagesPageByConversation: {},
  hasMoreMessagesByConversation: {},
  blockStatusByConversation: {},
  chatUsers: {},
  conversations: [],
  conversationsPage: 1,
  hasMoreConversations: false,
  unreadConversationsCount: 0,
  searchQuery: "",
  currentConversationId: null,
  loading: false,
  loadingMore: false,
  loadingConversations: false,
  sendingMessage: false,
  error: null,
  conversationsError: null,
};

export const $chatStore = createStore<ChatState>(initialState)
  .on(setCurrentConversation, (state, conversationId) => ({
    ...state,
    currentConversationId: conversationId,
    error: null,
  }))

  .on(clearConversationMessages, (state, conversationId) => {
    const { [conversationId]: _, ...rest } = state.messagesByConversation;
    return { ...state, messagesByConversation: rest };
  })

  .on(setConversationSearch, (state, searchQuery) => ({
    ...state,
    searchQuery,
    conversations: [],
    conversationsPage: 1,
  }))

  // Fetch conversation history
  .on(fetchConversationHistoryFx.pending, (state, pending) => ({
    ...state,
    loading: pending,
    error: pending ? null : state.error,
  }))
  .on(loadMoreMessagesRequested, (state) => ({
    ...state,
    loadingMore: true,
  }))
  .on(fetchConversationHistoryFx.doneData, (state, { conversationId, messages, otherUser, blockStatus, hasMore, page }) => {
    const existing = state.messagesByConversation[conversationId] || [];
    // Store is newest-first (API order). Older pages go at the end so that
    // after .reverse() in the UI they appear above (older = visually higher).
    const merged = page === 1 ? messages : [...existing, ...messages];
    return {
      ...state,
      messagesByConversation: {
        ...state.messagesByConversation,
        [conversationId]: merged,
      },
      messagesPageByConversation: {
        ...state.messagesPageByConversation,
        [conversationId]: page,
      },
      hasMoreMessagesByConversation: {
        ...state.hasMoreMessagesByConversation,
        [conversationId]: hasMore,
      },
      blockStatusByConversation: blockStatus !== undefined ? {
        ...state.blockStatusByConversation,
        [conversationId]: blockStatus,
      } : state.blockStatusByConversation,
      chatUsers: otherUser
        ? { ...state.chatUsers, [conversationId]: otherUser }
        : state.chatUsers,
      loading: false,
      loadingMore: false,
      error: null,
    };
  })
  .on(blockUserFx.doneData, (state, { conversationId, blockStatus }) => ({
    ...state,
    blockStatusByConversation: {
      ...state.blockStatusByConversation,
      [conversationId]: blockStatus,
    },
  }))
  .on(fetchConversationHistoryFx.failData, (state, error: any) => ({
    ...state,
    loading: false,
    loadingMore: false,
    error: error.message || "Failed to fetch messages",
  }))

  // Fetch conversations list
  .on(fetchConversationsFx.pending, (state, pending) => ({
    ...state,
    loadingConversations: pending,
    conversationsError: pending ? null : state.conversationsError,
  }))
  .on(fetchConversationsFx.doneData, (state, { conversations, hasMore, unreadCount, page }) => ({
    ...state,
    conversations: page === 1 ? conversations : [...state.conversations, ...conversations],
    conversationsPage: page,
    hasMoreConversations: hasMore,
    unreadConversationsCount: page === 1 ? unreadCount : state.unreadConversationsCount,
    loadingConversations: false,
    conversationsError: null,
  }))
  .on(fetchConversationsFx.failData, (state, error: any) => ({
    ...state,
    loadingConversations: false,
    conversationsError: error.message || "Failed to fetch conversations",
  }))

  // Send message
  .on(sendMessageFx.pending, (state, pending) => ({
    ...state,
    sendingMessage: pending,
    error: pending ? null : state.error,
  }))
  .on(sendMessageFx.doneData, (state, { conversationId, message }) => {
    if (!message) return { ...state, sendingMessage: false };
    const existing = state.messagesByConversation[conversationId] || [];
    return {
      ...state,
      messagesByConversation: {
        ...state.messagesByConversation,
        [conversationId]: [...existing, message],
      },
      sendingMessage: false,
      error: null,
    };
  })
  .on(sendMessageFx.failData, (state, error: any) => ({
    ...state,
    sendingMessage: false,
    error: error.message || "Failed to send message",
  }))

  // Mark as read
  .on(markConversationAsReadFx.doneData, (state, conversationId) => ({
    ...state,
    conversations: state.conversations.map((conv) =>
      conv.id.toString() === conversationId
        ? { ...conv, read_by: [...conv.read_by] } // optimistic — actual read_by updated on next fetch
        : conv
    ),
  }))

  // Delete conversation
  .on(deleteConversationFx.doneData, (state, conversationId) => {
    const { [conversationId]: _msgs, ...restMessages } = state.messagesByConversation;
    const { [conversationId]: _page, ...restPages } = state.messagesPageByConversation;
    const { [conversationId]: _more, ...restHasMore } = state.hasMoreMessagesByConversation;
    const { [conversationId]: _user, ...restUsers } = state.chatUsers;
    return {
      ...state,
      conversations: state.conversations.filter((c) => c.id.toString() !== conversationId),
      messagesByConversation: restMessages,
      messagesPageByConversation: restPages,
      hasMoreMessagesByConversation: restHasMore,
      chatUsers: restUsers,
      currentConversationId: state.currentConversationId === conversationId ? null : state.currentConversationId,
    };
  });

// ============ Samples ============

sample({
  clock: fetchConversationHistoryRequested,
  fn: (conversationId) => ({ conversationId, page: 1 }),
  target: fetchConversationHistoryFx,
});
sample({
  clock: loadMoreMessagesRequested,
  source: $chatStore,
  fn: (state, conversationId) => ({
    conversationId,
    page: (state.messagesPageByConversation[conversationId] ?? 1) + 1,
  }),
  target: fetchConversationHistoryFx,
});
sample({ clock: deleteConversationRequested, target: deleteConversationFx });
sample({ clock: blockUserRequested, source: $chatStore, fn: (state, payload) => ({ ...payload, conversationId: state.currentConversationId ?? "" }), target: blockUserFx });
sample({
  clock: fetchConversationsRequested,
  source: $chatStore,
  fn: (state) => ({ page: 1, search: state.searchQuery }),
  target: fetchConversationsFx,
});
sample({
  clock: loadMoreConversationsRequested,
  source: $chatStore,
  fn: (state) => ({ page: state.conversationsPage + 1, search: state.searchQuery }),
  target: fetchConversationsFx,
});
sample({
  clock: setConversationSearch,
  fn: (search) => ({ page: 1, search }),
  target: fetchConversationsFx,
});
sample({ clock: sendMessageRequested, target: sendMessageFx });
sample({ clock: markConversationAsReadRequested, target: markConversationAsReadFx });

// Refetch history (page 1) after sending to get server-assigned id/uuid
sample({
  clock: sendMessageFx.doneData,
  fn: ({ conversationId }) => ({ conversationId, page: 1 }),
  target: fetchConversationHistoryFx,
});

// Refetch conversation list after sending so the inbox order/preview updates
sample({
  clock: sendMessageFx.doneData,
  source: $chatStore,
  fn: (state) => ({ page: 1, search: state.searchQuery }),
  target: fetchConversationsFx,
});

// Refetch conversation list to update global unread count after marking as read
sample({
  clock: markConversationAsReadFx.doneData,
  source: $chatStore,
  fn: (state) => ({ page: 1, search: state.searchQuery }),
  target: fetchConversationsFx,
});

// ============ Derived Stores ============

export const $currentConversationMessages = $chatStore.map((state) => {
  if (!state.currentConversationId) return [];
  return state.messagesByConversation[state.currentConversationId] || [];
});

export const $unreadConversationsCount = $chatStore.map((state) => state.unreadConversationsCount);

export const $chatLoading = $chatStore.map((state) => state.loading);
export const $chatLoadingMore = $chatStore.map((state) => state.loadingMore);
export const $conversationsLoading = $chatStore.map((state) => state.loadingConversations);
export const $sendingMessage = $chatStore.map((state) => state.sendingMessage);

export const $currentConversationHasMore = $chatStore.map((state) => {
  if (!state.currentConversationId) return false;
  return state.hasMoreMessagesByConversation[state.currentConversationId] ?? false;
});

export const $currentConversationBlockStatus = $chatStore.map((state): BlockStatus => {
  if (!state.currentConversationId) return "not_blocked";
  return state.blockStatusByConversation[state.currentConversationId] ?? "not_blocked";
});

export const $blockingUser = blockUserFx.pending;
